// boundary-ok — only imported from Header.tsx, already a client boundary
import { useTranslations } from 'next-intl';
import { useEffect, useState, type CSSProperties } from 'react';
import CustomLink from 'components/shared/CustomLink';
import { CustomImage } from 'components/shared/custom-image/CustomImage';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import {
  MORE_MENU_KEY,
  hasShowcaseIntro,
  isFeaturedCardVisible,
  isMenuLinkVisible,
  isSecondaryCtaVisible,
  resolveLabelKey,
  type MenuSection,
  type NavItem,
  type NavMenuItem,
} from './navConfig';
import styles from './MegaMenu.module.css';
import { headerLinkAttribution } from './headerAnalytics';

/** Intro flexes; card tracks grow up to cardPx and shrink when the panel is narrow. */
const showcaseColumns = (
  hasIntro: boolean,
  cardCount: number,
  introMin: number,
  cardPx: number,
) =>
  hasIntro
    ? `minmax(${introMin}px, 1fr) repeat(${cardCount}, minmax(0, ${cardPx}px))`
    : `repeat(${cardCount}, minmax(0, 1fr))`;

const cls = (...names: (string | false | undefined)[]) =>
  names.filter(Boolean).join(' ');

/**
 * The contract `Header.tsx` renders against. The panel for tab `k` must keep
 * `id="mega-panel-${k}"` — the header's Escape, click-away and ArrowDown focus
 * handling all look it up by that id — and the "More" panel's id uses
 * `MORE_MENU_KEY`.
 */
export type MegaMenuProps = {
  openKey: string | null;
  items: NavItem[];
  /** The hamburger "More" panel's labelled link columns. */
  moreSections?: MenuSection[];
  /**
   * The card looks identical in both states; only its horizontal placement
   * differs — centred under the pill when scrolled, left-aligned under the tabs
   * at the top.
   */
  scrolled?: boolean;
  /** Switch the open panel to a tab (the More panel's Primary list). */
  onSwitchTab?: (key: string) => void;
};

export const MegaMenu = ({
  openKey,
  items,
  moreSections = [],
  scrolled = false,
  onSwitchTab,
}: MegaMenuProps) => {
  const isOpen = openKey !== null;
  const menuItems = items.filter((i): i is NavMenuItem => i.kind === 'menu');

  // Latch the last opened key so the exit animation has content to fade out.
  const [renderKey, setRenderKey] = useState<string | null>(openKey);
  useEffect(() => {
    if (openKey !== null) setRenderKey(openKey);
  }, [openKey]);

  return (
    <div
      className={cls(
        styles.surface,
        isOpen && styles.open,
        scrolled && styles.scrolled,
      )}
    >
      <div
        className={cls(styles.wrap, isOpen && styles.open)}
        aria-hidden={!isOpen}
        inert={!isOpen}
      >
        <div className={styles.clip}>
          <div className={styles.stack}>
            {menuItems.map((item) => (
              <div
                key={item.key}
                role="region"
                id={`mega-panel-${item.key}`}
                aria-labelledby={`mega-trigger-${item.key}`}
                className={cls(
                  styles.panel,
                  item.key === renderKey && styles.active,
                )}
                inert={item.key !== openKey}
              >
                <ShowcaseContent item={item} open={isOpen} />
              </div>
            ))}
            {moreSections.length > 0 && (
              <div
                role="region"
                id={`mega-panel-${MORE_MENU_KEY}`}
                aria-labelledby={`mega-trigger-${MORE_MENU_KEY}`}
                className={cls(
                  styles.panel,
                  MORE_MENU_KEY === renderKey && styles.active,
                )}
                inert={MORE_MENU_KEY !== openKey}
              >
                <MoreMenuContent
                  sections={moreSections}
                  items={menuItems}
                  open={isOpen}
                  onSwitchTab={onSwitchTab}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * "Showcase" panel layout (Figma 5695:406 / 6007:122791): an intro column (tab
 * label + tagline) beside large image cards carrying a title, a one-line
 * description and CTA pills pinned to the card's bottom-left. Every shipping
 * tab uses this layout — main's alternative rail+sections layout only ever
 * served the hidden Science/Partnerships tabs, so it is not ported.
 */
const ShowcaseContent = ({
  item,
  open,
}: {
  item: NavMenuItem;
  open: boolean;
}) => {
  const t = useTranslations('common');
  const { region } = useRegionLocale();

  const cards = (item.featured ?? []).filter((card) =>
    isFeaturedCardVisible(card, region),
  );
  const hasIntro = hasShowcaseIntro(item, cards.length);

  return (
    <div
      className={cls(styles.showcase, open && styles.open)}
      style={
        {
          '--cols-lg': showcaseColumns(hasIntro, cards.length, 280, 462),
          '--cols-sm': showcaseColumns(hasIntro, cards.length, 220, 380),
        } as CSSProperties
      }
    >
      {hasIntro && item.taglineKey && (
        <div className={styles.intro}>
          <span className={styles.introLabel}>
            {t(item.introLabelKey ?? item.labelKey)}
          </span>
          <p className={styles.tagline}>{t(item.taglineKey)}</p>
        </div>
      )}
      {cards.map((card) => {
        const label = t(resolveLabelKey(card, region));
        const rawCities = card.citiesKey ? t.raw(card.citiesKey) : [];
        const cities = Array.isArray(rawCities) ? rawCities : [];
        const secondaryCta =
          card.secondaryCta && isSecondaryCtaVisible(card.secondaryCta, region)
            ? card.secondaryCta
            : undefined;
        return (
          <article
            key={card.labelKey}
            className={cls(
              styles.card,
              card.lightPhoto && styles.cardLight,
              card.alwaysLightPhoto && styles.cardAlwaysLight,
              card.comingSoon && styles.cardComingSoon,
            )}
            data-showcase-card
            aria-label={
              card.comingSoon
                ? t('header.v3.menu.performanceLab.comingSoonAria')
                : undefined
            }
          >
            <div className={styles.imageWrap}>
              <CustomImage
                src={card.image}
                alt={card.imageAlt ?? label}
                fill
                sizes="(max-width: 1279px) 380px, 462px"
                style={{
                  objectFit: card.imageFit ?? 'cover',
                  objectPosition: 'center',
                }}
              />
            </div>
            <div
              className={cls(styles.copy, card.comingSoon && styles.copyTop)}
            >
              {card.comingSoon ? (
                <>
                  <span className={styles.comingSoonBadge}>{label}</span>
                  {cities.length > 0 && (
                    <ul className={styles.cityList}>
                      {cities.map((city) => (
                        <li key={city}>{city}</li>
                      ))}
                    </ul>
                  )}
                </>
              ) : (
                <>
                  <span className={styles.cardTitle}>{label}</span>
                  {card.descriptionKey && (
                    <span className={styles.cardDesc}>
                      {t(card.descriptionKey)}
                      {card.footnoteKey ? `\n${t(card.footnoteKey)}` : null}
                    </span>
                  )}
                </>
              )}
              {card.ctaKey && (
                <div className={styles.ctas}>
                  <CustomLink
                    href={card.href}
                    className={cls(
                      styles.cta,
                      styles[card.ctaVariant ?? 'outline'],
                    )}
                    aria-label={`${t(card.ctaKey)} ${label}`}
                    {...headerLinkAttribution({
                      label: t(card.ctaKey),
                      buttonType: card.buttonType,
                    })}
                  >
                    {t(card.ctaKey)}
                  </CustomLink>
                  {secondaryCta && (
                    <CustomLink
                      href={secondaryCta.href}
                      className={cls(styles.cta, styles[secondaryCta.variant])}
                      aria-label={`${t(secondaryCta.labelKey)} ${label}`}
                      {...headerLinkAttribution({
                        label: t(secondaryCta.labelKey),
                        buttonType: secondaryCta.buttonType,
                      })}
                    >
                      {t(secondaryCta.labelKey)}
                    </CustomLink>
                  )}
                </div>
              )}
            </div>
          </article>
        );
      })}
    </div>
  );
};

/**
 * The desktop hamburger's "More" panel (Figma 6007:123525): a left "Primary"
 * list of the top-level tab labels — clicking one switches the open mega-menu
 * to that tab, since the design gives them no hrefs — beside the
 * right-anchored labelled link columns.
 */
const MoreMenuContent = ({
  sections,
  items,
  open,
  onSwitchTab,
}: {
  sections: MenuSection[];
  items: NavMenuItem[];
  open: boolean;
  onSwitchTab?: (key: string) => void;
}) => {
  const t = useTranslations('common');
  const { region } = useRegionLocale();

  return (
    <div className={cls(styles.more, open && styles.open)}>
      <ul className={styles.morePrimary}>
        {items.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              className={styles.morePrimaryButton}
              aria-controls={`mega-panel-${item.key}`}
              /* While the More panel is open these buttons coexist with the
                 identically-labelled nav triggers in the a11y tree
                 (visibility:hidden only removes INACTIVE panels), so they need
                 a distinct accessible name. */
              aria-label={t('header.v3.openNamedMenu', {
                label: t(item.labelKey),
              })}
              onClick={() => onSwitchTab?.(item.key)}
            >
              {t(item.labelKey)}
            </button>
          </li>
        ))}
      </ul>
      <div className={styles.moreColumns}>
        {sections.map((section, idx) => {
          const visibleLinks = section.links.filter((l) =>
            isMenuLinkVisible(l, region),
          );
          if (visibleLinks.length === 0) return null;
          return (
            <div className={styles.moreGroup} key={section.headingKey ?? idx}>
              <h4 className={styles.moreHeading}>{t(section.headingKey!)}</h4>
              <ul className={styles.linkList}>
                {visibleLinks.map((link) => {
                  const label = t(resolveLabelKey(link, region));
                  return (
                    <li key={link.labelKey}>
                      <CustomLink
                        href={link.href}
                        className={styles.moreLink}
                        {...headerLinkAttribution({ label })}
                      >
                        {label}
                      </CustomLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
};
